import React from "react";
import { withStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { colors } from "../../constants/colors";

// MUI Components
import Grid from "@material-ui/core/Grid";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";

// Highcharts Graphs
import CsiPlot from "./CsiPlot";
import BmodeHeatmap from "./BmodeHeatmap";
import PhotoDisplay from "./Photo";
import Notes from "./Notes";

// Actions
import classNames from "classnames";
import { userToggleSampleIncluded, changeSampleLocation } from "../../actions";
import { csiTypes } from "../../constants/constants";
import { customTheme } from "../../constants/customTheme";

// const whyDidYouRender = require("@welldone-software/why-did-you-render/dist/no-classes-transpile/umd/whyDidYouRender.min.js");
// whyDidYouRender(React);

const styles = {
  root: (props) => ({
    flex: "1 1 auto",
    height: "100%",
    zIndex: 1,
    overflowY: "auto",
    ...customTheme(props.theme).customScrollBar,
  }),
  gridRoot: {
    flex: "1 1 auto",
    height: "100%",
    zIndex: 1,
    flexDirection: "row",
    justifyContent: "center",
  },
  headerSpace: {
    height: "35px",
  },
  FormControlLabel: {
    margin: "30px",
  },
  switch: {
    color: (props) => colors[props.theme].tertiary,
    "& .MuiSwitch-colorPrimary.Mui-checked": {
      color: (props) => colors[props.theme].tertiary,
    },
    "& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track": {
      backgroundColor: (props) => colors[props.theme].tertiary,
    },
  },
  outerElement: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
  },
  changeLocationItem: {
    padding: "20px",
  },
  dialogRoot: (props) => ({
    ...customTheme(props.theme).dialog.root,
    minHeight: "300px",
  }),
  buttonContainer: customTheme().dialog.buttonContainer,
  buttonOutlined: (props) => ({
    ...customTheme(props.theme).buttonOutlined,
  }),
  buttonPrimary: (props) => ({
    ...customTheme(props.theme).buttonPrimary,
  }),
  buttonSecondary: (props) => ({
    ...customTheme(props.theme).buttonSecondary,
    textTransform: "unset",
  }),
  formControl: {
    width: "225px",
  },
  contentContainer: (props) => ({
    ...customTheme().dialog.contentContainer,
    ...customTheme(props.theme).customScrollBar,
    alignItems: "center",
    justifyContent: "center",
    overflowY: "auto",
  }),
  contentItem: customTheme().dialog.contentItem,
  sampleIdDisplay: {
    opacity: "0.5",
    fontSize: "0.875rem",
  },
};

class Results extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      changeLocationDialog: false,
      currentLocationId: this.props.locationId,
    };
  }

  handleToggleIncluded = () => {
    this.props.userToggleSampleIncluded(this.props.sampleId);
  };

  handleToggleDialog = () => {
    this.setState({
      ...this.state,
      changeLocationDialog: !this.state.changeLocationDialog,
      currentLocationId: this.props.locationId,
    });
  };

  handleSetNewLocation = () => {
    this.props.changeSampleLocation(
      this.props.sampleId,
      this.state.currentLocationId
    );
    this.setState({ ...this.state, changeLocationDialog: false });
  };

  render() {
    const { classes } = this.props;

    // Show study info if no sample is selected
    // or the selected sample is still processing && !this.props.showLocationInfo
    if (this.props.sampleId) {
      return (
        <div className={classes.outerElement}>
          <div className={classes.headerSpace} />
          <div className={classes.root}>
            <Grid
              container
              direction="column"
              justify="space-between"
              alignItems="stretch"
              className={classes.gridRoot}
            >
              {/* BMODE SAMPLE */}
              {this.props.type === "BMODE" && <BmodeHeatmap />}

              {/* PHOTOGRAPH SAMPLE */}
              {this.props.type === "PICTURE" && <PhotoDisplay />}

              {/* CSI SAMPLE */}
              {csiTypes.includes(this.props.type) && <CsiPlot />}

              {/* NOTES */}
              <Notes prompt="SAMPLE" />

              <Grid
                item
                container
                justify="flex-start"
                className={classes.changeLocationItem}
              >
                <Button
                  className={
                    this.props.type === "BMODE"
                      ? classes.buttonSecondary
                      : classes.buttonPrimary
                  }
                  variant="contained"
                  color={this.props.type === "BMODE" ? "secondary" : "primary"}
                  onClick={() => this.handleToggleDialog()}
                  style={{ textTransform: "unset" }}
                  disabled={
                    !this.props.includeInStudy ||
                    this.props.acquiring ||
                    !this.props.samplesDone
                  }
                >
                  Change Sample Location
                </Button>

                <Dialog
                  disableBackdropClick
                  disableEscapeKeyDown
                  open={this.state.changeLocationDialog}
                  PaperProps={{ className: classNames(classes.dialogRoot) }}
                >
                  <Typography
                    className={classes.title}
                    variant="h4"
                    color="textPrimary"
                    align="center"
                  >
                    Change Sample Location
                  </Typography>
                  <DialogContent className={classes.contentContainer}>
                    <FormControl className={classes.formControl}>
                      <InputLabel>Location</InputLabel>
                      <Select
                        value={this.state.currentLocationId}
                        onChange={(e) =>
                          this.setState({
                            ...this.state,
                            currentLocationId: e.target.value,
                          })
                        }
                        MenuProps={{
                          className: classNames(classes.menuScroll),
                        }}
                      >
                        {Object.keys(this.props.allLocations).map((locId) => {
                          return (
                            <MenuItem
                              className={classes.menuItem}
                              key={locId}
                              value={locId}
                            >
                              {this.props.allLocations[locId]["name"]}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </DialogContent>
                  <DialogActions className={classes.buttonContainer}>
                    <Button
                      onClick={() => this.handleToggleDialog()}
                      variant="outlined"
                      className={classes.buttonOutlined}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => this.handleSetNewLocation()}
                      className={classes.buttonPrimary}
                      variant="contained"
                      color="primary"
                      disabled={
                        this.state.currentLocationId === this.props.locationId
                      }
                    >
                      Move Sample
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid>

              {/* Include in study */}
              <Grid item container justify="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={this.props.includeInStudy}
                      disabled={this.props.locked}
                      value="checkedB"
                      className={
                        this.props.type === "PICTURE" ? classes.switch : null
                      }
                      color={
                        this.props.type === "BMODE" ? "secondary" : "primary"
                      }
                      onChange={() => this.handleToggleIncluded()}
                    />
                  }
                  label="Include In Study"
                  labelPlacement="start"
                  className={classes.FormControlLabel}
                />
              </Grid>

              {/* SAMPLE ID */}
              <Grid
                item
                style={{ userSelect: "text" }}
                container
                justify="center"
              >
                <Typography className={classes.sampleIdDisplay}>
                  Sample ID: {this.props.sampleId}
                </Typography>
              </Grid>
            </Grid>
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  }
}

const mapStateToProps = (state) => {
  const sample = state.samples.byId[state.samples.selectedSampleId];
  let allValidLocations = {};
  Object.keys(state.protocol.locations).forEach((locId) => {
    if (!state.protocol.locations[locId].removed) {
      if (sample && csiTypes.includes(sample.type)) {
        // Example:Do NOT allow an STL type sample to move to a SINGLE/SWIPE/MMODE location
        if (sample.type === state.protocol.locations[locId].parameters_id) {
          allValidLocations[locId] = {
            name: state.protocol.locations[locId].name,
          };
        }
      } else {
        allValidLocations[locId] = {
          name: state.protocol.locations[locId].name,
        };
      }
    }
  });
  return {
    sampleId: sample ? sample.id : null,
    type: sample ? sample.type : null,
    includeInStudy: sample ? sample.includeInStudy : null,
    locked: state.studyInfo.locked,
    theme: state.utility.theme,
    locationId: sample ? sample.locationId : null,
    allLocations: allValidLocations,
    acquiring: state.device.acquiring,
    samplesDone: state.utility.samplesDone,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      userToggleSampleIncluded,
      changeSampleLocation,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(Results));
